/**
 * @module engine/shapes/defs
 * @summary Built-in shape definitions (default size + library category).
 * @description
 *   Pure data: the ~90 built-in equipment symbols and annotations. Each key
 *   is a shape `type`; the value gives its default width/height and the
 *   library group `g` it appears under. The matching draw function lives in
 *   `drawFns.ts` under the same key. Custom symbols are merged on top at
 *   runtime by the ShapeRegistry (see registry.ts).
 * @ported-from JCC PFD Engineering Tool v1.1.html lines 1033-1156 (verbatim).
 */
import type { ShapeDef } from '../../types';

export const DEFS: Record<string, ShapeDef> = {
  // Storage
  silo: { label: 'Silo', w: 50, h: 100, g: 'Storage & Silos' },
  hopper: { label: 'Day Bin / Hopper', w: 60, h: 70, g: 'Storage & Silos' },
  htank: { label: 'Tank (Horizontal)', w: 90, h: 50, g: 'Storage & Silos' },
  vtank: { label: 'Tank (Vertical)', w: 50, h: 80, g: 'Storage & Silos' },
  bulkbag: { label: 'Bulk Bag (FIBC)', w: 55, h: 65, g: 'Storage & Silos' },
  ibc: { label: 'IBC Container', w: 50, h: 60, g: 'Storage & Silos' },
  supermixer: { label: 'Super Mixer (Hot)', w: 70, h: 90, g: 'Storage & Silos' },
  coldmixer: { label: 'Cold Mixer', w: 120, h: 60, g: 'Storage & Silos' },
  buffertank: { label: 'Buffer Tank', w: 55, h: 90, g: 'Storage & Silos' },
  // Feeders
  rotaryvalve: { label: 'Rotary Valve', w: 34, h: 28, g: 'Feeders & Conveyors' },
  liwfeeder: { label: 'LIW Feeder', w: 70, h: 95, g: 'Feeders & Conveyors' },
  volfeeder: { label: 'Vol. Feeder', w: 50, h: 45, g: 'Feeders & Conveyors' },
  screwconv: { label: 'Screw Conveyor', w: 90, h: 28, g: 'Feeders & Conveyors' },
  beltconv: { label: 'Belt Conveyor', w: 90, h: 25, g: 'Feeders & Conveyors' },
  bagdump: { label: 'Bag Dump Stn.', w: 55, h: 50, g: 'Feeders & Conveyors' },
  transferconv: { label: 'Transfer Conveyor', w: 110, h: 35, g: 'Feeders & Conveyors' },
  crusherconv: { label: 'Crusher Conveyor', w: 110, h: 35, g: 'Feeders & Conveyors' },
  washcontainer: { label: 'Washing Container', w: 80, h: 70, g: 'Feeders & Conveyors' },
  drytank: { label: 'Dry Tank', w: 70, h: 80, g: 'Feeders & Conveyors' },
  basketconv: { label: 'Basket Conveyor', w: 90, h: 40, g: 'Feeders & Conveyors' },
  sidefeeder: { label: 'Side Feeder', w: 80, h: 65, g: 'Feeders & Conveyors' },
  manualdebag: { label: 'Manual Debagging', w: 90, h: 80, g: 'Feeders & Conveyors' },
  hoistdebag: { label: 'Hoist Debagging Station', w: 70, h: 110, g: 'Feeders & Conveyors' },
  debaghopper: { label: 'Debagging Hopper', w: 65, h: 95, g: 'Feeders & Conveyors' },
  debagging: { label: 'Debagging Hopper', w: 70, h: 75, g: 'Feeders & Conveyors' },
  rawmatsilo: { label: 'Raw Material Feeder Silo', w: 65, h: 110, g: 'Feeders & Conveyors' },
  scsfeeder: { label: 'Single Screw Feeder', w: 90, h: 40, g: 'Feeders & Conveyors' },
  tcsfeeder: { label: 'Twin Screw Feeder', w: 90, h: 40, g: 'Feeders & Conveyors' },
  liquidfeeder: { label: 'Liquid Feeder', w: 55, h: 75, g: 'Feeders & Conveyors' },
  forcedfeeder: { label: 'Forced Feeder', w: 60, h: 110, g: 'Feeders & Conveyors' },
  granulessilo: { label: 'Granules Silo', w: 70, h: 110, g: 'Feeders & Conveyors' },
  powdersilo: { label: 'Powder Silo', w: 70, h: 120, g: 'Feeders & Conveyors' },
  // Processing
  tse: { label: 'Twin-Screw Ext.', w: 150, h: 46, g: 'Processing' },
  sse: { label: 'Single-Screw Ext.', w: 130, h: 38, g: 'Processing' },
  coknead: { label: 'BUSS Co-Kneader', w: 110, h: 44, g: 'Processing' },
  gearpump: { label: 'Gear Pump', w: 36, h: 36, g: 'Processing' },
  screenchange: { label: 'Screen Changer', w: 26, h: 36, g: 'Processing' },
  staticmix: { label: 'Static Mixer', w: 45, h: 24, g: 'Processing' },
  pelletscanner: { label: 'Pellet Scanner', w: 60, h: 50, g: 'Processing' },
  // Dies
  crosshead: { label: 'Crosshead Die', w: 55, h: 55, g: 'Dies & Heads' },
  stranddie: { label: 'Strand Die', w: 32, h: 50, g: 'Dies & Heads' },
  uwdie: { label: 'UW Die', w: 36, h: 40, g: 'Dies & Heads' },
  flatdie: { label: 'Flat Sheet Die', w: 26, h: 60, g: 'Dies & Heads' },
  // Cable Downstream
  coolingtrough: { label: 'Cooling Trough', w: 100, h: 30, g: 'Cable Downstream' },
  cathaul: { label: 'Caterpillar Haul-off', w: 70, h: 35, g: 'Cable Downstream' },
  sparktester: { label: 'Spark Tester', w: 42, h: 30, g: 'Cable Downstream' },
  odgauge: { label: 'OD Laser Gauge', w: 22, h: 32, g: 'Cable Downstream' },
  winder: { label: 'Traversing Winder', w: 50, h: 50, g: 'Cable Downstream' },
  accumulator: { label: 'Accumulator', w: 32, h: 55, g: 'Cable Downstream' },
  preheater: { label: 'Conductor Preheater', w: 42, h: 30, g: 'Cable Downstream' },
  eccmon: { label: 'Eccentricity Monitor', w: 26, h: 32, g: 'Cable Downstream' },
  capstan: { label: 'Capstan / Payoff', w: 36, h: 36, g: 'Cable Downstream' },
  // Compounding Downstream
  strandbath: { label: 'Strand Bath', w: 100, h: 30, g: 'Compounding Downstream' },
  airknife: { label: 'Air Knife / Dryer', w: 36, h: 30, g: 'Compounding Downstream' },
  strandpellet: { label: 'Strand Pelletizer', w: 56, h: 45, g: 'Compounding Downstream' },
  uwpellet: { label: 'UW Pelletizer', w: 65, h: 80, g: 'Compounding Downstream' },
  pelletdryer: { label: 'Pellet Dryer', w: 50, h: 55, g: 'Compounding Downstream' },
  classifier: { label: 'Pellet Classifier', w: 50, h: 55, g: 'Compounding Downstream' },
  fgsilo: { label: 'FG Silo', w: 45, h: 90, g: 'Compounding Downstream' },
  bagstation: { label: 'Bagging Station', w: 56, h: 60, g: 'Compounding Downstream' },
  centdryer: { label: 'Centrifugal Dryer', w: 55, h: 60, g: 'Compounding Downstream' },
  vibroscreen: { label: 'Vibro Screen', w: 60, h: 40, g: 'Compounding Downstream' },
  dedusting: { label: 'De-Dusting Unit', w: 50, h: 55, g: 'Compounding Downstream' },
  hotcutpellet: { label: 'Hot / Dry Cut Pelletizer', w: 70, h: 80, g: 'Compounding Downstream' },
  waterringpellet: { label: 'Water Ring Pelletizer', w: 75, h: 65, g: 'Compounding Downstream' },
  centdewater: { label: 'Centrifugal Dryer', w: 55, h: 85, g: 'Compounding Downstream' },
  aircoolingvib: { label: 'Air Cooling Vibrator', w: 100, h: 45, g: 'Compounding Downstream' },
  fluidizedbed: { label: 'Fluidized Bed Dryer', w: 55, h: 90, g: 'Compounding Downstream' },
  divertervalve: { label: 'Diverter Valve', w: 32, h: 72, g: 'Compounding Downstream' },
  connectingtube: { label: 'Connecting Tube', w: 24, h: 60, g: 'Compounding Downstream' },
  degassing: { label: 'Degassing Unit', w: 62, h: 55, g: 'Compounding Downstream' },
  // Pneumatic Conveying
  blower: { label: 'Blower', w: 50, h: 46, g: 'Pneumatic Conveying' },
  rootsblower: { label: 'Roots Blower', w: 46, h: 46, g: 'Pneumatic Conveying' },
  diverter2: { label: '2-Way Diverter', w: 32, h: 32, g: 'Pneumatic Conveying' },
  diverter3: { label: '3-Way Diverter', w: 36, h: 36, g: 'Pneumatic Conveying' },
  filterreceiver: { label: 'Filter Receiver', w: 46, h: 66, g: 'Pneumatic Conveying' },
  cyclone: { label: 'Cyclone', w: 40, h: 60, g: 'Pneumatic Conveying' },
  elbow90: { label: 'Elbow 90°', w: 28, h: 28, g: 'Pneumatic Conveying' },
  vacloader: { label: 'Vacuum Loader', w: 50, h: 80, g: 'Pneumatic Conveying' },
  vacreceiver: { label: 'Vacuum Receiver', w: 70, h: 45, g: 'Pneumatic Conveying' },
  // Valves & Instruments
  gatevalve: { label: 'Gate Valve', w: 22, h: 22, g: 'Valves & Instruments' },
  ballvalve: { label: 'Ball Valve', w: 22, h: 22, g: 'Valves & Instruments' },
  checkvalve: { label: 'Check Valve', w: 22, h: 22, g: 'Valves & Instruments' },
  ctrlvalve: { label: 'Control Valve', w: 28, h: 32, g: 'Valves & Instruments' },
  pgauge: { label: 'Pressure Gauge', w: 22, h: 22, g: 'Valves & Instruments' },
  tsensor: { label: 'Temp. Sensor', w: 18, h: 26, g: 'Valves & Instruments' },
  flowmeter: { label: 'Flow Meter', w: 30, h: 22, g: 'Valves & Instruments' },
  prv: { label: 'Pressure Relief', w: 22, h: 28, g: 'Valves & Instruments' },
  butterflyvalve: { label: 'Butterfly Valve', w: 24, h: 24, g: 'Valves & Instruments' },
  solenoidvalve: { label: 'Solenoid Valve', w: 24, h: 30, g: 'Valves & Instruments' },
  levelswitch: { label: 'Level Switch', w: 20, h: 28, g: 'Valves & Instruments' },
  // Utilities
  centpump: { label: 'Centrifugal Pump', w: 36, h: 36, g: 'Utilities' },
  chiller: { label: 'Chiller Unit', w: 65, h: 55, g: 'Utilities' },
  compressor: { label: 'Compressor', w: 50, h: 45, g: 'Utilities' },
  motor: { label: 'Motor', w: 32, h: 32, g: 'Utilities' },
  controlpanel: { label: 'Control Panel', w: 56, h: 70, g: 'Utilities' },
  metaldet: { label: 'Metal Detector', w: 36, h: 30, g: 'Utilities' },
  pprollers: { label: 'Rollers', w: 100, h: 35, g: 'Film & PP Line' },
  stretchoven: { label: 'Stretching Oven', w: 110, h: 45, g: 'Film & PP Line' },
  quenchbath: { label: 'Quenching Bath', w: 100, h: 50, g: 'Film & PP Line' },
  takeuprollers: { label: 'Take Up Rollers', w: 80, h: 35, g: 'Film & PP Line' },
  takeupwinder: { label: 'Take Up Winder', w: 120, h: 55, g: 'Film & PP Line' },
  heatex: { label: 'Heat Exchanger', w: 70, h: 35, g: 'Utilities' },
  vacpump: { label: 'Vacuum Pump', w: 45, h: 38, g: 'Utilities' },
  weighbatcher: { label: 'Weigh Batcher', w: 65, h: 55, g: 'Utilities' },
  airfilter: { label: 'Air Filter', w: 30, h: 42, g: 'Utilities' },
  nitrogen: { label: 'N2 Gas Supply', w: 36, h: 60, g: 'Utilities' },
  bubblecolumn: { label: 'Bubble Column Reactor', w: 55, h: 160, g: 'Utilities' },
  // Annotations
  textlabel: { label: 'Text Label', w: 80, h: 24, g: 'Annotations' },
  zonebox: { label: 'Zone Box', w: 160, h: 110, g: 'Annotations' },
  processarrow: { label: 'Process Arrow', w: 80, h: 30, g: 'Annotations' },
  titleblock: { label: 'Title Block', w: 220, h: 70, g: 'Annotations' },
};

/** Ordered list of the built-in library categories (first-seen order in DEFS). */
export const BUILTIN_CATEGORY_ORDER: string[] = (() => {
  const seen: string[] = [];
  for (const key of Object.keys(DEFS)) {
    const g = DEFS[key]!.g;
    if (!seen.includes(g)) seen.push(g);
  }
  return seen;
})();
